import React from 'react';
import { Box, IconButton, Popover, TextField } from '@mui/material';
import { IconX, IconLink } from '@tabler/icons-react';
import { getFavicon, getDomain, isValidUrl } from '../../utils/urlHelpers';

/**
 * Link input component with popover
 */
const LinkInput = ({
  link,
  setLink,
  linkError,
  setLinkError,
  linkAnchorEl,
  setLinkAnchorEl,
  showLinkInput,
  setShowLinkInput,
}) => {
  const handleRemove = () => setLink('');

  return (
    <Box position="relative" display="flex" alignItems="center">
      <IconButton
        size="small"
        aria-label={link ? 'Edit link' : 'Add link'}
        onClick={e => {
          setLinkAnchorEl(e.currentTarget);
          setShowLinkInput(true);
        }}
        sx={{ bgcolor: link ? '#e3f2fd' : 'transparent', borderRadius: '8px' }}
      >
        <IconLink size={20} color={link ? '#1976d2' : '#757575'} />
      </IconButton>

      <Popover
        open={showLinkInput}
        anchorEl={linkAnchorEl}
        onClose={() => setShowLinkInput(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1, minWidth: 220 } }}
      >
        <TextField
          autoFocus
          placeholder="Paste a link (e.g. https://...)"
          variant="outlined"
          size="small"
          value={link}
          onChange={e => {
            setLink(e.target.value);
            setLinkError(!!e.target.value && !isValidUrl(e.target.value));
          }}
          onBlur={() => setLinkError(!!link && !isValidUrl(link))}
          error={linkError}
          helperText={linkError ? 'Enter a valid URL' : ''}
          sx={{ width: 200 }}
          InputProps={{
            endAdornment: link && (
              <IconButton size="small" onClick={handleRemove}>
                <IconX size={16} />
              </IconButton>
            )
          }}
        />
      </Popover>

      {link && isValidUrl(link) && (
        <Box
          ml={1}
          display="flex"
          alignItems="center"
          sx={{
            borderRadius: '8px',
            bgcolor: '#f0f2f5',
            px: 1.5,
            py: 0.5,
            minHeight: 32,
            maxHeight: 32,
            maxWidth: 180,
            overflow: 'hidden',
            fontSize: '0.95rem',
            gap: 1,
            cursor: 'pointer',
            transition: 'background 0.2s',
            ':hover .remove-link-btn': { opacity: 1 },
          }}
          tabIndex={0}
        >
          {getFavicon(link) ? (
            <img
              src={getFavicon(link)}
              alt="Favicon"
              style={{ width: 18, height: 18, borderRadius: 4, marginRight: 6, background: '#fff' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <IconLink size={16} style={{ marginRight: 6, color: '#757575' }} />
          )}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90 }}>
            {getDomain(link)}
          </span>
          <IconButton
            size="small"
            onClick={handleRemove}
            className="remove-link-btn"
            sx={{
              ml: 0.5,
              p: 0.5,
              bgcolor: 'white',
              boxShadow: 1,
              opacity: 0,
              transition: 'opacity 0.2s',
              width: 20,
              height: 20,
              zIndex: 2,
              ':hover': { opacity: 1 },
              ':focus': { opacity: 1 },
            }}
            aria-label="Remove link"
          >
            <IconX size={13} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default LinkInput;
